import React from 'react'
import qs, { ParsedQs } from 'qs'

import useSavedValue from '@SonarCX/hooks/useSavedValue'

export enum TabState {
  NONE,
  SUBTASKS,
  COMMENTS
}

type TaskCardStateContextType = {
  filterID: keyof LocalTask
  setFilterID: React.Dispatch<React.SetStateAction<keyof LocalTask>>
  extraFilters: string
  setExtraFilters: (val: string) => void
  parsedExtra: ParsedQs
  tabState: Record<string, TabState>
  setTabState: (task: LocalTask, state: TabState) => void
}

const TaskCardStateContext = React.createContext<TaskCardStateContextType>(null)

type TaskCardStateProviderProps = {
  children: React.ReactNode
}

const FILTER_STORAGE_KEY = 'task_card_filter'

const TaskCardStateProvider: React.FC<TaskCardStateProviderProps> = ({
  children
}) => {
  const [filterID, setFilterID] = useSavedValue<keyof LocalTask>({
    key: FILTER_STORAGE_KEY,
    defaultValue: '"category_id"'
  })
  const [extraFilters, _setExtraFilters] = React.useState('')
  const [parsedExtra, setParsedExtra] = React.useState<ParsedQs>({})
  const [tabState, _setTabState] = React.useState<
    TaskCardStateContextType['tabState']
  >({})

  const setExtraFilters = React.useCallback(
    (val: string) => {
      // Convert string query to parameters and store
      const parsed = qs.parse(val)
      setParsedExtra(parsed)

      // Remove current filter from query
      const copy = { ...parsed }
      delete copy[`${filterID}s`]
      copy.include_subtasks = 'true'
      const asQuery = qs.stringify(copy, { arrayFormat: 'brackets' })
      _setExtraFilters(asQuery)
    },
    [filterID]
  )

  const setTabState: TaskCardStateContextType['setTabState'] =
    React.useCallback((task, state) => {
      _setTabState(old => {
        if (!(task.id in old)) {
          return {
            ...old,
            [task.id]: state
          }
        }

        const oldVal = old[task.id]
        return {
          ...old,
          [task.id]: oldVal === state ? TabState.NONE : state
        }
      })
    }, [])

  return (
    <TaskCardStateContext.Provider
      value={{
        filterID,
        setFilterID,
        extraFilters,
        setExtraFilters,
        parsedExtra,
        tabState,
        setTabState
      }}
    >
      {children}
    </TaskCardStateContext.Provider>
  )
}

export default TaskCardStateProvider

export const useTaskCardState = () => React.useContext(TaskCardStateContext)
